/**
 * Petición de autorización OAuth que un cliente MCP (el conector de claude.ai)
 * manda a `/oauth/authorize`.
 *
 * Los parámetros tienen que llegar a la API **tal cual**, carácter a carácter:
 * `state` vuelve al cliente y `code_challenge` se compara con el verificador.
 * El router no sirve para leerlos: interpreta cada valor como JSON y al volver
 * a escribir la URL convierte, por ejemplo, `state=1e3` en `state=1000`. Por
 * eso `main.tsx` guarda la query en crudo con
 * {@link captureAuthorizationRequest} antes de montar el router, y la pantalla
 * la lee de aquí. Se guarda también en `sessionStorage` para que sobreviva a
 * pasar por `/login` (incluso si esa página se recarga).
 */
import type { OAuthAuthorizationDecision, OAuthAuthorizationQuery } from '@fitness/api-client';

/** Ruta de la pantalla de consentimiento (`authorization_endpoint`). */
export const AUTHORIZE_PATH = '/oauth/authorize';

const STORAGE_KEY = 'fitness.oauth.request';

/** Los parámetros de una petición de autorización que se reenvían. */
const PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'scope',
  'resource',
] as const;

type Param = (typeof PARAMS)[number];

/** Almacén mínimo; `sessionStorage` en el navegador. */
export interface RequestStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

let captured: string | null = null;

function defaultStorage(): RequestStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // Safari en modo privado, o almacenamiento bloqueado.
    return null;
  }
}

/**
 * Guarda la query de `/oauth/authorize` si es la página que se está abriendo.
 * Hay que llamarla antes de que el router reescriba la URL.
 */
export function captureAuthorizationRequest(
  location: { pathname: string; search: string },
  storage: RequestStorage | null = defaultStorage(),
): void {
  if (location.pathname !== AUTHORIZE_PATH || location.search.length <= 1) return;
  captured = location.search;
  try {
    storage?.setItem(STORAGE_KEY, location.search);
  } catch {
    // Sin almacenamiento basta con la copia en memoria mientras no se recargue.
  }
}

/** La query en crudo de la petición en curso, o `null` si no hay ninguna. */
export function pendingAuthorizationRequest(
  storage: RequestStorage | null = defaultStorage(),
): string | null {
  if (captured !== null) return captured;
  try {
    return storage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Olvida la petición, una vez decidida. */
export function clearAuthorizationRequest(storage: RequestStorage | null = defaultStorage()): void {
  captured = null;
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // Nada que limpiar.
  }
}

/**
 * Si la página está dentro de un iframe. La pantalla de consentimiento no
 * enseña sus botones enmarcada: un iframe transparente sobre un botón señuelo
 * haría pulsar «Autorizar» sin saberlo (clickjacking). Es una defensa extra;
 * la de verdad son las cabeceras `frame-ancestors 'none'` y
 * `X-Frame-Options: DENY` de `deploy/Caddyfile`, que el navegador aplica antes
 * de ejecutar nada. Si leer `top` falla (un navegador que lo bloquea entre
 * orígenes), se da por enmarcada.
 */
export function isFramed(win: { top: unknown; self: unknown } = window): boolean {
  try {
    return win.top !== win.self;
  } catch {
    return true;
  }
}

/** Una petición ya leída, lista para mandarla a la API. */
export interface AuthorizationRequest {
  query: OAuthAuthorizationQuery;
  /** Algún parámetro venía repetido, cosa que OAuth prohíbe (RFC 6749 §3.1). */
  duplicated: boolean;
}

/** Lee la query en crudo. Los parámetros ausentes no se envían. */
export function parseAuthorizationRequest(raw: string): AuthorizationRequest {
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const query: Partial<Record<Param, string>> = {};
  let duplicated = false;
  for (const name of PARAMS) {
    const values = params.getAll(name);
    if (values.length > 1) duplicated = true;
    const value = values[0];
    if (value !== undefined) query[name] = value;
  }
  return { query, duplicated };
}

/** El cuerpo de `POST /v1/oauth/authorize` para una petición y una decisión. */
export function decisionBody(
  query: OAuthAuthorizationQuery,
  decision: OAuthAuthorizationDecision['decision'],
): OAuthAuthorizationDecision {
  return {
    responseType: query.response_type,
    clientId: query.client_id,
    redirectUri: query.redirect_uri,
    codeChallenge: query.code_challenge,
    codeChallengeMethod: query.code_challenge_method,
    state: query.state,
    scope: query.scope,
    resource: query.resource,
    decision,
  };
}
